import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  ButtonGroup,
  Select,
  MenuItem,
  Menu,
  FormControl,
  InputLabel,
  IconButton,
  Tooltip,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Radio,
  RadioGroup,
  FormControlLabel,
} from "@mui/material";
import { Refresh, ArrowDropDown } from "@mui/icons-material";
import { FilterState } from "../../types";
import { states } from "../../data/impactData";

interface HeaderProps {
  filters: FilterState;
  onFilterChange: (filters: Partial<FilterState>) => void;
  onRefresh: () => void;
  organization: string;
  stateOrOrganization: string;
  onExportPDF: () => void;
  isLoading: boolean;
  centerState: string;
  isExporting: boolean;
  hideStateFilter?: boolean;
}

const Header: React.FC<HeaderProps> = ({
  filters,
  onFilterChange,
  onRefresh,
  organization,
  stateOrOrganization,
  onExportPDF,
  isLoading,
  centerState,
  isExporting,
  hideStateFilter = false,
}) => {
  const [organizationName, setOrganizationName] = useState(organization);

  // -- Share dropdown / Embed dialog state --
  const [shareMenuAnchor, setShareMenuAnchor] = useState<null | HTMLElement>(
    null
  );
  const [embedDialogOpen, setEmbedDialogOpen] = useState(false);
  const [embedMode, setEmbedMode] = useState<"code" | "url">("code");
  const [embedWidth, setEmbedWidth] = useState<number>(1240);
  const [embedHeight, setEmbedHeight] = useState<number>(816);
  const [copyLabel, setCopyLabel] = useState("COPY TO CLIPBOARD");

  // The shareable URL is whatever the user is currently looking at.
  const buildShareUrl = () => window.location.href;

  // For embeds we point at the read-only export view, preserving query params.
  const buildEmbedUrl = () => {
    const url = new URL(window.location.href);
    if (!/\/public\/impact\/view(\/|$)/.test(url.pathname)) {
      url.pathname = url.pathname.replace(
        /\/public\/impact(\/[^/]*)?$/,
        (_match, suffix) => `/public/impact/view${suffix || ""}`
      );
    }
    return url.toString();
  };

  const buildEmbedCode = () =>
    `<iframe width="${embedWidth}" height="${embedHeight}" src="${buildEmbedUrl()}" frameborder="0" style="border:0" allowfullscreen></iframe>`;

  const handleShareMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setShareMenuAnchor(event.currentTarget);
  };

  const handleShareMenuClose = () => setShareMenuAnchor(null);

  const handleDownloadClick = () => {
    handleShareMenuClose();
    onExportPDF();
  };

  const handleCopyLink = async () => {
    handleShareMenuClose();
    try {
      await navigator.clipboard.writeText(buildShareUrl());
    } catch (err) {
      console.error("Copy link failed:", err);
    }
  };

  const handleOpenEmbed = () => {
    handleShareMenuClose();
    setEmbedDialogOpen(true);
  };

  const handleCopyEmbed = async () => {
    const text = embedMode === "code" ? buildEmbedCode() : buildEmbedUrl();
    try {
      await navigator.clipboard.writeText(text);
      setCopyLabel("COPIED!");
      setTimeout(() => setCopyLabel("COPY TO CLIPBOARD"), 1500);
    } catch (err) {
      console.error("Copy embed failed:", err);
    }
  };

  const useVerbiage = () => {
    if (filters.showOrgData) {
      return `Impact Overview for ${organization}`;
    } else if (filters.state !== "All States") {
      return `Impact Overview for ${stateOrOrganization}`;
    } else {
      return "National Impact Overview";
    }
  };

  useEffect(() => {
    if (organization && !organization.includes("'s")) {
      setOrganizationName(organization + "'s");
    }
  }, [organization]);

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        borderRadius: 2,
        background: "rgba(255, 255, 255, 0.9)",
        backdropFilter: "blur(10px)",
        border: "1px solid rgba(255, 255, 255, 0.2)",
        boxShadow:
          "0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Box>
          <Typography
            variant="h4"
            component="h1"
            sx={{
              color: "primary.main",
              fontWeight: 700,
              mb: 1,
            }}
          >
            {useVerbiage()}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Comprehensive analysis of CAC impact data and organizational metrics
          </Typography>
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          {organizationName && (
            <Button
              variant={filters.showOrgData ? "contained" : "outlined"}
              onClick={() => {
                onFilterChange({
                  showOrgData: !filters.showOrgData,
                  state: !filters.showOrgData ? centerState : "All States",
                });
              }}
              disabled={isLoading || isExporting}
              sx={{
                height: 44,
                borderRadius: 1,
                px: 3,
                py: 1,
                fontWeight: 500,
                boxSizing: "border-box",
                textTransform: "none",
                fontSize: "0.875rem",
                ...(filters.showOrgData
                  ? {
                      // Primary button styling (when active)
                      background:
                        "linear-gradient(265.82deg, #73269F 2.32%, #582B7A 98.45%)",
                      color: "#fff",
                      boxShadow: "0px 2px 8px rgba(73, 53, 196, 0.6)",
                      "&:hover": {
                        background:
                          "linear-gradient(0deg, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.1)), linear-gradient(265.82deg, #73269F 2.32%, #582B7A 98.45%)",
                        boxShadow: "0px 4px 16px rgba(73, 53, 196, 0.6)",
                      },
                      "&:active": {
                        background:
                          "linear-gradient(0deg, rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.2)), linear-gradient(265.82deg, #73269F 2.32%, #582B7A 98.45%)",
                        boxShadow: "0px 1px 4px rgba(73, 53, 196, 0.6)",
                      },
                      "&:disabled": {
                        background: "#f8f2ff",
                        color: "rgba(0, 0, 0, 0.38)",
                        borderColor: "rgba(0, 0, 0, 0.08)",
                        boxShadow: "none",
                        cursor: "not-allowed",
                      },
                    }
                  : {
                      // Secondary button styling (when inactive)
                      background: "transparent",
                      color: "#582B7A",
                      border: "2px solid rgba(0, 0, 0, 0.08)",
                      "&:hover": {
                        borderColor: "#582B7A",
                        background: "transparent",
                      },
                      "&:active": {
                        color: "#461662",
                        borderColor: "#461662",
                      },
                      "&:disabled": {
                        background: "white",
                        color: "rgba(0, 0, 0, 0.38)",
                        borderColor: "rgba(0, 0, 0, 0.08)",
                        boxShadow: "none",
                        cursor: "not-allowed",
                      },
                    }),
              }}
            >
              {organizationName} Impact
            </Button>
          )}

          {!hideStateFilter && (
            <FormControl
              variant="outlined"
              size="small"
              sx={{ minWidth: 150 }}
              disabled={isLoading || isExporting}
            >
              <InputLabel id="state-filter-label">Filter by</InputLabel>
              <Select
                labelId="state-filter-label"
                value={filters.state}
                onChange={(e) =>
                  onFilterChange({ state: e.target.value, showOrgData: false })
                }
                label="Filter by"
                sx={{ borderRadius: 1, fontSize: "0.875rem" }}
              >
                <MenuItem value="All States">All States</MenuItem>
                <hr />
                {states.map((state) => (
                  <MenuItem key={state.name} value={state.value}>
                    {state.name}
                  </MenuItem>
                ))}
                <hr />
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 1, textAlign: "center" }}
                >
                  U.S. Territories
                </Typography>
                <hr />
                <MenuItem value="us-pr">Puerto Rico</MenuItem>
              </Select>
            </FormControl>
          )}

          <Tooltip title="Refresh Data">
            <IconButton
              onClick={onRefresh}
              sx={{
                height: 44,
                width: 44,
                bgcolor: "background.paper",
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1,
                "&:hover": {
                  bgcolor: "action.hover",
                },
              }}
              disabled={isLoading || isExporting}
            >
              <Refresh />
            </IconButton>
          </Tooltip>

          <ButtonGroup
            variant="outlined"
            disabled={isLoading || isExporting}
            sx={{
              height: 44,
              borderRadius: 1,
              boxShadow: "none",
              "& .MuiButton-root": {
                background: "transparent",
                color: "#582B7A",
                textTransform: "none",
                fontWeight: 500,
                fontSize: "0.875rem",
                border: "2px solid rgba(0, 0, 0, 0.08)",
                "&:hover": {
                  borderColor: "#582B7A",
                  background: "transparent",
                },
                "&:active": {
                  color: "#461662",
                  borderColor: "#461662",
                },
                "&.Mui-disabled": {
                  background: "white",
                  color: "rgba(0, 0, 0, 0.38)",
                  borderColor: "rgba(0, 0, 0, 0.08)",
                  boxShadow: "none",
                },
              },
              "& .MuiButtonGroup-grouped:not(:last-of-type)": {
                borderRightColor: "rgba(0, 0, 0, 0.08)",
              },
            }}
          >
            <Button onClick={handleShareMenuOpen} sx={{ px: 3 }}>
              Share
            </Button>
            <Button
              onClick={handleShareMenuOpen}
              size="small"
              aria-label="Share options"
              sx={{ px: 1 }}
            >
              <ArrowDropDown />
            </Button>
          </ButtonGroup>

          <Menu
            anchorEl={shareMenuAnchor}
            open={Boolean(shareMenuAnchor)}
            onClose={handleShareMenuClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
          >
            <MenuItem onClick={handleDownloadClick}>Download</MenuItem>
            <MenuItem onClick={handleCopyLink}>Link</MenuItem>
            <MenuItem onClick={handleOpenEmbed}>Embed</MenuItem>
          </Menu>

          <Dialog
            open={embedDialogOpen}
            onClose={() => setEmbedDialogOpen(false)}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle sx={{ fontWeight: 700 }}>Embed Report</DialogTitle>
            <DialogContent>
              <RadioGroup
                row
                value={embedMode}
                onChange={(e) =>
                  setEmbedMode(e.target.value as "code" | "url")
                }
                sx={{ mb: 2 }}
              >
                <FormControlLabel
                  value="code"
                  control={<Radio />}
                  label="Embed Code"
                />
                <FormControlLabel
                  value="url"
                  control={<Radio />}
                  label="Embed URL"
                />
              </RadioGroup>

              <Typography variant="body2" sx={{ mb: 1 }}>
                Paste the following into your site:
              </Typography>
              <TextField
                multiline
                fullWidth
                minRows={3}
                maxRows={6}
                value={embedMode === "code" ? buildEmbedCode() : buildEmbedUrl()}
                InputProps={{
                  readOnly: true,
                  sx: { fontFamily: "monospace", fontSize: "0.8rem" },
                }}
                sx={{ mb: 2 }}
              />

              {embedMode === "code" && (
                <Box sx={{ display: "flex", gap: 2 }}>
                  <TextField
                    label="Width (px)"
                    type="number"
                    variant="standard"
                    value={embedWidth}
                    onChange={(e) =>
                      setEmbedWidth(Math.max(1, Number(e.target.value) || 0))
                    }
                    sx={{ flex: 1 }}
                  />
                  <TextField
                    label="Height (px)"
                    type="number"
                    variant="standard"
                    value={embedHeight}
                    onChange={(e) =>
                      setEmbedHeight(Math.max(1, Number(e.target.value) || 0))
                    }
                    sx={{ flex: 1 }}
                  />
                </Box>
              )}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button
                onClick={() => setEmbedDialogOpen(false)}
                sx={{ color: "text.primary" }}
              >
                DONE
              </Button>
              <Button variant="contained" onClick={handleCopyEmbed}>
                {copyLabel}
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      </Box>
    </Paper>
  );
};

export default Header;
